import {ZadSaleSettlement, ZadSaleSettlementStatus} from '../../../models/zad'

export const ZAD_SALE_SETTLEMENT_I18N: Record<ZadSaleSettlementStatus, string> = {
  UNPAID: 'ZAD.SALES.SETTLEMENT_UNPAID',
  PARTIAL: 'ZAD.SALES.SETTLEMENT_PARTIAL',
  SETTLED: 'ZAD.SALES.SETTLEMENT_SETTLED',
}

export function normalizeZadSaleSettlementStatus(
  raw?: string | null
): ZadSaleSettlementStatus | null {
  const value = String(raw || '').toUpperCase()
  if (value === 'UNPAID' || value === 'PARTIAL' || value === 'SETTLED') return value
  return null
}

function settlementRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === 'object' ? (value as Record<string, any>) : null
}

export function readZadSaleSettlement(source?: unknown): ZadSaleSettlement | null {
  if (source == null || source === '') return null
  if (typeof source === 'string') {
    const status = normalizeZadSaleSettlementStatus(source)
    return status
      ? {status, saleAmount: Number.NaN, paidAmount: Number.NaN, remainingAmount: Number.NaN}
      : null
  }
  const obj = settlementRecord(source)
  if (!obj) return null
  const ownStatus = normalizeZadSaleSettlementStatus(obj.status)
  const nested = ownStatus || obj.saleAmount != null ? obj : obj.settlement
  if (nested == null || nested === '') return null
  if (typeof nested === 'string') {
    const status = normalizeZadSaleSettlementStatus(nested)
    return status
      ? {status, saleAmount: Number.NaN, paidAmount: Number.NaN, remainingAmount: Number.NaN}
      : null
  }
  const raw = settlementRecord(nested)
  if (!raw) return null
  const status = normalizeZadSaleSettlementStatus(raw.status || raw.settlement)
  if (!status) return null
  const hasAmounts =
    raw.saleAmount != null && raw.paidAmount != null && raw.remainingAmount != null
  return {
    status,
    saleAmount: hasAmounts ? Number(raw.saleAmount) : Number.NaN,
    paidAmount: hasAmounts ? Number(raw.paidAmount) : Number.NaN,
    remainingAmount: hasAmounts ? Number(raw.remainingAmount) : Number.NaN,
    allocated: raw.allocated,
    outstanding: raw.outstanding,
    saleTotal: raw.saleTotal,
    validatedReturnsTotal: raw.validatedReturnsTotal,
    netSaleTotal: raw.netSaleTotal,
    paidAt: optionalString(raw.paidAt),
    lastPaymentAt: optionalString(raw.lastPaymentAt),
    lastPaymentMethod: optionalString(raw.lastPaymentMethod),
    paymentMethods: Array.isArray(raw.paymentMethods)
      ? raw.paymentMethods.filter((m: unknown) => typeof m === 'string' && m)
      : [],
    paymentCount: Number.isInteger(raw.paymentCount) ? raw.paymentCount : undefined,
    overdue: raw.overdue === true,
  }
}

function optionalString(value: unknown): string | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString()
  return typeof value === 'string' && value.trim() ? value : null
}

/**
 * Backend `overdue` flag, shown only while money is still owed. Never recomputed
 * from dueDate here: Africa/Tunis day boundaries are the backend's responsibility.
 */
export function isZadSaleSettlementOverdue(settlement?: ZadSaleSettlement | null): boolean {
  return Boolean(settlement && settlement.status !== 'SETTLED' && settlement.overdue === true)
}

/**
 * "Modifier l'échéance" mirrors the backend gate: same capability as sale
 * validation, VALIDATED sale, and something still owed.
 */
export function canEditZadSaleDueDate(
  sale: {status?: string | null; settlement?: unknown} | null | undefined,
  canValidateSales: boolean
): boolean {
  if (!sale || !canValidateSales || !isZadSaleSettlementApplicable(sale.status)) return false
  const settlement = readZadSaleSettlement(sale)
  return Boolean(settlement && settlement.status !== 'SETTLED')
}

/** A due date is a payment term for what is still owed; it is noise once settled. */
export function shouldShowZadSaleDueDate(
  settlement: ZadSaleSettlement | null | undefined,
  dueDate?: string | null
): boolean {
  return Boolean(settlement && settlement.status !== 'SETTLED' && zadCalendarDayYmd(dueDate))
}

/**
 * Calendar day of a backend dueDate (YYYY-MM-DD or UTC-midnight ISO). Read from
 * the string itself so a browser timezone can never shift the day.
 */
export function zadCalendarDayYmd(value?: string | null): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || '').trim())
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null
}

export function formatZadCalendarDay(value?: string | null): string | null {
  const ymd = zadCalendarDayYmd(value)
  if (!ymd) return null
  const [y, m, d] = ymd.split('-')
  return `${d}/${m}/${y}`
}

const ZAD_BUSINESS_TZ = 'Africa/Tunis'

function isUtcMidnight(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  )
}

/**
 * Payment instant for display. Manual payments carry a calendar day stored at UTC
 * midnight (no real time), POS collections carry the actual instant.
 */
export function formatZadPaymentMoment(
  value?: string | null
): {date: string; time: string | null} | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  if (isUtcMidnight(date)) {
    const day = formatZadCalendarDay(date.toISOString())
    return day ? {date: day, time: null} : null
  }
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: ZAD_BUSINESS_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const part = (type: string) => parts.find((p) => p.type === type)?.value || ''
  return {
    date: `${part('day')}/${part('month')}/${part('year')}`,
    time: `${part('hour')}:${part('minute')}`,
  }
}

/** Africa/Tunis business day (YYYY-MM-DD) of an instant, for date-input bounds. */
export function zadBusinessDayYmd(value: string | Date = new Date()): string | null {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZAD_BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

export function isZadSaleSettlementApplicable(status?: string | null): boolean {
  return String(status || '').toUpperCase() === 'VALIDATED'
}

export function zadSaleSettlementHasAmounts(settlement?: ZadSaleSettlement | null): boolean {
  return Boolean(
    settlement &&
      Number.isFinite(settlement.saleAmount) &&
      Number.isFinite(settlement.paidAmount) &&
      Number.isFinite(settlement.remainingAmount)
  )
}

export function zadSaleSettlementBadgeClass(status?: ZadSaleSettlementStatus | null): string {
  if (status === 'PARTIAL') return 'badge badge-light-warning'
  if (status === 'SETTLED') return 'badge badge-light-success'
  if (status === 'UNPAID') return 'badge badge-light-primary'
  return 'badge badge-light'
}
